<!--
  An ElAutocomplete using ElUtteranceInput
  Suggestions are using the style the utterance variables

  As close as possible from element-ui el-autocomplete
  https://github.com/ElemeFE/element/blob/dev/packages/autocomplete/src/autocomplete.vue

  Props:
  - localVariables: an array of local variables suggestions
-->

<template>
  <div
    class="el-autocomplete el-utterance-autocomplete"
    v-clickoutside="close"
    aria-haspopup="listbox"
    role="combobox"
    :aria-expanded="suggestionVisible"
    :aria-owns="id"
  >
    <el-utterance-input
      ref="input"
      v-bind="[$props, $attrs]"
      v-on="$listeners"
      @clear="handleClear"
      @keydown.up.native.prevent="highlight(highlightedIndex - 1)"
      @keydown.down.native.prevent="handleDown"
      @keydown.enter.native="handleKeyEnter"
      @keydown.native.tab="close"
      @suggestion="handleSuggestion"
    >
      <template slot="prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </template>
      <template slot="append" v-if="$slots.append">
        <slot name="append"></slot>
      </template>
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template>
    </el-utterance-input>
    <el-mobile-suggestions
      :width="suggestionWidth"
      visible-arrow
      :class="[popperClass ? popperClass : '']"
      :popper-options="popperOptions"
      :append-to-body="popperAppendToBody"
      ref="suggestions"
      :placement="placement"
      :id="id">
      <li
        v-for="(item, index) in suggestions"
        :key="index"
        :class="{'highlighted': highlightedIndex === index}"
        @click="select(item)"
        :id="`${id}-item-${index}`"
        role="option"
        :aria-selected="highlightedIndex === index"
      >
        <slot :item="item">
          <span :class="['el-utterance-autocomplete__item', itemClass]">
            <span class="el-utterance-autocomplete__prefix">{{ itemPrefix
            }}</span><span class="el-utterance-autocomplete__left">{{ item.left
            }}</span><strong class="el-utterance-autocomplete__match">{{ item.match
            }}</strong><span class="el-utterance-autocomplete__right">{{ item.right
            }}</span>
          </span>
          <span class="el-utterance-autocomplete__count"
            v-if="'count' in item"> ({{item.count}})</span>
        </slot>
      </li>
    </el-mobile-suggestions>
  </div>
</template>
<script>
import debounce from 'throttle-debounce/debounce'
import ElUtteranceInput from './ElUtteranceInput.vue'
import Clickoutside from 'element-ui/lib/utils/clickoutside'
import ElMobileSuggestions from './ElMobileSuggestions.vue'
import Emitter from 'element-ui/lib/mixins/emitter'
import { generateId } from 'element-ui/lib/utils/util'
import Focus from 'element-ui/lib/mixins/focus'
import { filterSuggestions } from '../../utils/filter.js'

export default {
  name: 'ElUtteranceAutocomplete',

  mixins: [Emitter, Focus('input')],

  inheritAttrs: false,

  componentName: 'ElAutocomplete',

  components: {
    ElUtteranceInput,
    ElMobileSuggestions,
  },

  directives: { Clickoutside },

  props: {
    // the array of suggestions for each type of variable
    localVariables: Array,
    // same as el-autocomplete
    popperClass: String,
    popperOptions: Object,
    placeholder: String,
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
    name: String,
    size: String,
    value: String,
    maxlength: Number,
    minlength: Number,
    autofocus: Boolean,
    // fetchSuggestions: Function,
    triggerOnFocus: {
      type: Boolean,
      default: true,
    },
    customItem: String,
    selectWhenUnmatched: {
      type: Boolean,
      default: false,
    },
    prefixIcon: String,
    suffixIcon: String,
    label: String,
    debounce: {
      type: Number,
      default: 300,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    hideLoading: Boolean,
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    // suggestion width
    suggestionWidth: {
      type: Number,
      default: 150,
    },
  },
  data () {
    return {
      // the query object from ElUtteranceInput
      query: null,
      // same as el-autocomplete
      activated: false,
      suggestions: [],
      loading: false,
      highlightedIndex: -1,
      suggestionDisabled: false,
    }
  },
  computed: {
    // same as el-autocomplete
    suggestionVisible () {
      const suggestions = this.suggestions
      let isValidData = Array.isArray(suggestions) && suggestions.length > 0
      return (isValidData || this.loading) && this.activated
    },
    id () {
      return `el-autocomplete-${generateId()}`
    },
    // used for ofr styling suggestions
    itemPrefix () {
      return '@'
    },
    itemClass () {
      return this.query && ('el-utterance-autocomplete__item-' + this.query.type)
    },
  },
  watch: {
    suggestionVisible (val) {
      // this.broadcast('ElAutocompleteSuggestions', 'visible', [val, this.$refs.input.$refs.contenteditable.offsetWidth])

      // needs to pass the preferred position of the suggestion
      this.broadcast('ElAutocompleteSuggestions', 'visible', [val, this.$refs.input.$refs.input.offsetWidth, this.query && this.query.x])
    },
  },
  methods: {
    insert (...args) { return this.$refs.input.insert(...args) },
    unreference (...args) { return this.$refs.input.unreference(...args) },
    setRef (...args) { return this.$refs.input.setRef(...args) },
    setText (...args) { return this.$refs.input.setText(...args) },
    getData (query) {
      if (this.suggestionDisabled) {
        return
      }
      // more simple fetch
      this.suggestions = filterSuggestions(query.query, this.localVariables || [])
      // this.loading = true
      // this.fetchSuggestions(queryString, (suggestions) => {
      //   this.loading = false
      //   if (this.suggestionDisabled) {
      //     return
      //   }
      //   if (Array.isArray(suggestions)) {
      //     this.suggestions = suggestions
      //   } else {
      //     console.error('[Element Error][Autocomplete]autocomplete suggestions must be an array')
      //   }
      // })
    },
    handleClear () {
      this.activated = false
      // make suggestion available for a "down" press
      if (this.query) this.query.input = false
      this.$emit('clear')
    },
    close (e) {
      this.activated = false
      // make suggestion available for a "down" press
      if (this.query) this.query.input = false
    },
    handleKeyEnter (e) {
      if (this.suggestionVisible && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
        e.preventDefault()
        this.select(this.suggestions[this.highlightedIndex])
      } else if (this.selectWhenUnmatched) {
        this.$emit('select', { value: this.value })
        this.$nextTick(_ => {
          this.suggestions = []
          this.highlightedIndex = -1
        })
      }
    },
    select (item) {
      // replace the cursor by the selected varibale
      this.$refs.input.setText(this.query.key, item.value)
      // same as el-autocomplete
      this.$emit('select', item)
      this.$nextTick(_ => {
        this.suggestions = []
        this.highlightedIndex = -1
      })
    },
    highlight (index) {
      if (!this.suggestionVisible || this.loading) { return }
      if (index < 0) {
        this.highlightedIndex = -1
        return
      }
      if (index >= this.suggestions.length) {
        index = this.suggestions.length - 1
      }
      const suggestion = this.$refs.suggestions.$el.querySelector('.el-autocomplete-suggestion__wrap')
      const suggestionList = suggestion.querySelectorAll('.el-autocomplete-suggestion__list li')

      let highlightItem = suggestionList[index]
      let scrollTop = suggestion.scrollTop
      let offsetTop = highlightItem.offsetTop

      if (offsetTop + highlightItem.scrollHeight > (scrollTop + suggestion.clientHeight)) {
        suggestion.scrollTop += highlightItem.scrollHeight
      }
      if (offsetTop < scrollTop) {
        suggestion.scrollTop -= highlightItem.scrollHeight
      }
      this.highlightedIndex = index
      this.$el.querySelector('.el-input__inner').setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
    },
    // for "down" press, reopens the suggestion
    // if the cursor is at an interesting place
    handleDown () {
      if (!this.activated && this.query) {
        this.query.input = true
        this.activated = true
        this.debouncedGetData(this.query)
      } else this.highlight(this.highlightedIndex + 1)
    },
    // for suggestions from ElUtteranceInput
    // opens the suggestions if comes from an input
    handleSuggestion (query) {
      this.query = query
      if (query && query.input) {
        this.activated = true
        this.debouncedGetData(query)
      } else {
        this.activated = false
      }
    },
  },
  mounted () {
    this.debouncedGetData = debounce(this.debounce, this.getData)
    this.$on('item-click', item => {
      this.select(item)
    })
    let $input = this.$el.querySelector('.el-input__inner')
    $input.setAttribute('role', 'textbox')
    $input.setAttribute('aria-autocomplete', 'list')
    $input.setAttribute('aria-controls', 'id')
    $input.setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
  },
  beforeDestroy () {
    this.$refs.suggestions.$destroy()
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  /* avoid weird CSS bugs with height */
  .el-autocomplete.el-utterance-autocomplete {
    height: $--input-height;
  }
  /* style of utterance variables */
  .el-utterance-autocomplete__item-entity {
    background-color: $entity-bg-color;
  }
  .el-utterance-autocomplete__count {
    color: $--color-text-placeholder;
  }
</style>
